import React, { useEffect, useRef, useState } from "react";
import clsx from "clsx";
import { Card, Box, Typography } from "@curves/ui-base";
import { MeasurementTitle, MeasurementItem } from "@Local/Measurement/Components/Common";
import { InbodyScoreBox, CIDBox, BodyTypeMapBox, PartAnalysisSection, CurveAndBalanceSection } from "@Local/Measurement/Components/ResultPage";
import { bodyCompositionData, obesityAnalysisData, bodyMeasurementsData, researchParametersData } from "@Local/Measurement/data/";
import { useMeasurementContext } from "@Local/Measurement/Context/MeasurementContext";
import useMemberInbodyApi from "@Apis/useMemberInbodyApi";
import useEmployeeInbodyApi from "@Apis/useEmployeeInbodyApi";
import { isEmpty, localUser } from "@Util/utils";
import { MEMBER_SITE_LOGON_TYPE } from "@Constants/dataEnums";
import WeightManagementCard from "@Local/Measurement/Components/WeightManagementCard/WeightManagementCard";
import { parseDate } from "@Util/moment";
import MeasureDateCard from "@Local/Measurement/Components/MeasureDateCard/MeasureDateCard";
import useShowLoading from "@Util/hook/useShowLoading";

/** 明細頁：依 context 的 measureID（最新一次量身 ID）顯示文字數據 */
const MeasurementDetailPage = () => {
	const { measureID, measureOptions } = useMeasurementContext();
	const { getMemberInbodyResultApi, getMemberInbodyCompareRecentApi, getMemberInbodyComparePastApi } = useMemberInbodyApi();
	const { getEmployeeInbodyResultApi } = useEmployeeInbodyApi();
	const [data, setData] = useState(null);
	const currentMeasureID = useRef(measureID);
	const showLoading = useShowLoading();
	const [isLoaded, setIsLoaded] = useState(false);

	// 比較類型
	const COMPARE_RECENT = "recent"; // 與上次比較
	const COMPARE_PAST = "past"; // 自定義比較
	const [compareType, setCompareType] = useState(COMPARE_RECENT);
	const [compareData, setCompareData] = useState(null);
	const [compareID, setCompareID] = useState(null);

	const { logonType } = localUser.get() || {};

	const mergeRemoteData = (itemData = {}, remoteData = {}) => {
		const _itemData = { ...itemData };
		const normalMinValue = remoteData[_itemData?.normalMinValueKey];
		const normalMaxValue = remoteData[_itemData?.normalMaxValueKey];
		const minValue = remoteData[_itemData?.minValueKey];
		const maxValue = remoteData[_itemData?.maxValueKey];
		const value = remoteData[_itemData?.key];
		const deltaValue = remoteData[_itemData?.deltaValueKey];

		if (normalMinValue && normalMaxValue) {
			_itemData.normalRange = [normalMinValue, normalMaxValue];
		}
		if (minValue) {
			_itemData.minValue = minValue;
		}
		if (maxValue) {
			_itemData.maxValue = maxValue;
		}
		if (value || typeof value === "number") {
			_itemData.value = String(value);
		}
		if (deltaValue) {
			_itemData.deltaValue = deltaValue;
		}
		return _itemData;
	};

	const doMemberInbodyResultApi = async () => {
		if (!isEmpty(data) && currentMeasureID.current === measureID) return;
		setIsLoaded(false);
		showLoading(true);
		try {
			const resp =
				logonType === MEMBER_SITE_LOGON_TYPE.MEMBER
					? await getMemberInbodyResultApi(measureID)
					: logonType === MEMBER_SITE_LOGON_TYPE.EMPLOYEE
						? await getEmployeeInbodyResultApi(measureID)
						: null;

			if (resp) {
				setData(resp);
				currentMeasureID.current = measureID;
			} else {
				setData(null);
			}
		} finally {
			showLoading(false);
			setIsLoaded(true);
		}
	};

	const fetchCompareData = async (type, id = null) => {
		setCompareData(null);
		if (type === COMPARE_RECENT) {
			const resp = await getMemberInbodyCompareRecentApi();
			if (resp) setCompareData(resp);
		} else if (type === COMPARE_PAST && id != null) {
			const resp = await getMemberInbodyComparePastApi(id);
			if (resp) setCompareData(resp);
		}
	};

	const handleCompareTypeSwitch = type => {
		if (type === compareType) return;
		setCompareType(type);
		if (type === COMPARE_RECENT) {
			fetchCompareData(COMPARE_RECENT);
		} else {
			const oldestID = measureOptions[measureOptions.length - 1]?.value;
			if (oldestID) {
				setCompareID(oldestID);
				fetchCompareData(COMPARE_PAST, oldestID);
			}
		}
	};

	const handleCompareIDChange = event => {
		const value = event.target.value;
		setCompareID(value);
		fetchCompareData(COMPARE_PAST, value);
	};

	useEffect(
		() => {
			if (measureID) {
				setCompareType(COMPARE_RECENT);
				setCompareData(null);
				setCompareID(null);
				doMemberInbodyResultApi();
				fetchCompareData(COMPARE_RECENT);
			}
		},
		// eslint-disable-next-line
		[measureID],
	);

	useEffect(
		() => {
			if (!isEmpty(measureOptions)) {
				const oldestID = measureOptions[measureOptions.length - 1]?.value;
				if (oldestID) {
					setCompareID(oldestID);
				}
			}
		},
		// eslint-disable-next-line
		[measureOptions],
	);

	return (
		<>
			{isLoaded && !isEmpty(data) && (
				<Box className={"container"}>
					<Box className="measurement">
						<div className="part-analysis-section__controller part-analysis-section-sticky">
							<button type="button" className={clsx({ active: compareType === COMPARE_RECENT })} onClick={() => handleCompareTypeSwitch(COMPARE_RECENT)}>
								與上次比較
							</button>
							<button type="button" className={clsx({ active: compareType === COMPARE_PAST })} onClick={() => handleCompareTypeSwitch(COMPARE_PAST)}>
								自定義比較
							</button>
						</div>
						{compareType === COMPARE_PAST && (
							<Box className={"measurement-group"}>
								<MeasureDateCard
									recentMeasureDate={parseDate(data?.measureDate, "YYYY-MM-DD HH:mm")}
									measureOptions={measureOptions}
									compareID={compareID}
									onCompareIDChange={handleCompareIDChange}
								/>
							</Box>
						)}
						{compareData && (
							<div>
								{compareType === COMPARE_RECENT && (
									<Box className={"measurement-group"}>
										<MeasureDateCard
											recentMeasureDate={parseDate(data?.measureDate, "YYYY-MM-DD HH:mm")}
											deltaMeasureDate={parseDate(compareData?.deltaMeasureDate, "YYYY-MM-DD HH:mm")}
										/>
									</Box>
								)}
								<Box className={"measurement-group"}>
									<MeasurementTitle title={"InBody 評分"} />
									<InbodyScoreBox score={data?.inbodyScore} deltaInbodyScore={compareData?.deltaInbodyScore} />
								</Box>
								<Box className={"measurement-group"}>
									<MeasurementTitle title={"體重管理指南"} />
									<WeightManagementCard
										currentWeightKg={data?.weight}
										targetWeightKg={data?.targetWeight}
										targetWeightColorTheme="default"
										weightControl={data?.weightControl}
										bfmControl={data?.bfmControl}
										ffmControl={data?.ffmControl}
									/>
								</Box>
								<Box className={"measurement-group"}>
									<MeasurementTitle title={"肌肉脂肪分析"} />
									<Box className={"measurement-group-content"}>
										<CIDBox sourceData={data} className={"pb-2"} deltaSourceData={compareData} />
									</Box>
								</Box>
								<Box className={"measurement-group"}>
									<MeasurementTitle title={"肥胖分析"} />
									<Box className={"measurement-group-content"}>
										{obesityAnalysisData.map((item, index) => {
											const itemData = mergeRemoteData(item, data);
											const deltaItemData = mergeRemoteData(item, compareData);
											return <MeasurementItem key={index} {...itemData} deltaValue={deltaItemData.deltaValue} />;
										})}
									</Box>
								</Box>
								<Box className={"measurement-group pb-2"}>
									<MeasurementTitle title={"身體類型"} />
									<Box className={"measurement-group-content"}>
										<BodyTypeMapBox
											sourceData={{
												bmi: data?.bmi,
												pbf: data?.pbf,
											}}
											pastData={{
												bmi: compareData?.pastBMI,
												pbf: compareData?.pastPBF,
											}}
										/>
									</Box>
								</Box>
								<Box className={"measurement-group"}>
									<MeasurementTitle title={"身體組成分析"} />
									<Box className={"measurement-group-content"}>
										{bodyCompositionData.map((item, index) => {
											const itemData = mergeRemoteData(item, data);
											const deltaItemData = mergeRemoteData(item, compareData);
											return <MeasurementItem key={index} {...itemData} deltaValue={deltaItemData.deltaValue} />;
										})}
									</Box>
								</Box>
								<Box className={"measurement-group pb-2"}>
									<MeasurementTitle title={"部位分析"} />
									<Box className={"measurement-group-content mb-2"}>
										<PartAnalysisSection className={"px-2"} sourceData={data} />
									</Box>
									<Box className={"measurement-group-content"}>
										<CurveAndBalanceSection className={"px-2"} sourceData={data} compareData={compareData} />
									</Box>
								</Box>
								{/* <Box className={"measurement-group"}>
									<MeasurementTitle title={"身體平衡"} />
									<Box className={"measurement-group-content--2"}>
										{bodyEquilibriumData.map((item, index) => {
											const itemData = mergeRemoteData(item, data);
											itemData.value = parseMeasurementDataEvalString(itemData.value);
											return <MeasurementItem className={"body-equilibrium-item"} key={index} {...itemData} />;
										})}
									</Box>
								</Box> */}
								<Box className={"measurement-group"}>
									<MeasurementTitle title={"部位別圍度"} />
									<Box className={"measurement-group-content--2"}>
										{bodyMeasurementsData.map((item, index) => {
											const itemData = mergeRemoteData(item, data);
											const deltaItemData = mergeRemoteData(item, compareData);
											return <MeasurementItem key={index} {...itemData} deltaValue={deltaItemData.deltaValue} />;
										})}
									</Box>
								</Box>
								<Box className={"measurement-group"}>
									<MeasurementTitle title={"研究參數"} />
									<Box className={"measurement-group-content"}>
										{researchParametersData.researchParametersBarData.map((item, index) => {
											const itemData = mergeRemoteData(item, data);
											const deltaItemData = mergeRemoteData(item, compareData);
											return <MeasurementItem key={index} {...itemData} deltaValue={deltaItemData.deltaValue} />;
										})}
									</Box>
									<Box className={"measurement-group-content--2"}>
										{researchParametersData.researchParametersCardData.map((item, index) => {
											const itemData = mergeRemoteData(item, data);
											const deltaItemData = mergeRemoteData(item, compareData);
											return <MeasurementItem key={index} {...itemData} deltaValue={deltaItemData.deltaValue} />;
										})}
									</Box>
								</Box>

								{/* TODO: 身體組成歷程記錄隱藏，因Dashboard頁有趨勢 */}
								{/* <Box className={"measurement-group"}>
									<MeasurementTitle title={"身體組成歷程記錄"} />
									<Box className={"measurement-group-content"}>
										<BodyCompositionTable className={"px-3 pb-2"} sourceData={data?.measureHistoryList} />
									</Box>
								</Box> */}
							</div>
						)}
					</Box>
				</Box>
			)}
			{isLoaded && isEmpty(data) && (
				<Card className="page-center m-4">
					<Typography variant={"h6"} color={"error"}>
						無任何量身紀錄
					</Typography>
				</Card>
			)}
		</>
	);
};

export default MeasurementDetailPage;
